import React from "react";
import { Zap, CheckCircle, XCircle, Loader, LayoutGrid } from "lucide-react";
import { DashboardPageLayout } from "../../../components/layout/DashboardPageLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { ButtonLink } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { useAppToast } from "../../../components/feedback/AppToastProvider";
import type {
  AdminJobDefinition,
  AdminJobBusinessOption,
  AdminJobsData,
  AdminRouteMap,
} from "../types";

type DispatchState = "idle" | "loading" | "success" | "error";

type JobRowProps = {
  job: AdminJobDefinition;
  selectedBusinessId: number | null;
  dispatchUrl: string;
  csrfToken: string;
};

const JobRow: React.FC<JobRowProps> = ({
  job,
  selectedBusinessId,
  dispatchUrl,
  csrfToken,
}) => {
  const [state, setState] = React.useState<DispatchState>("idle");
  const [resultMessage, setResultMessage] = React.useState("");
  const { pushToast } = useAppToast();

  const dispatch = async () => {
    if (!selectedBusinessId) return;
    setState("loading");
    setResultMessage("");

    try {
      const res = await fetch(dispatchUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          business_id: selectedBusinessId,
          job_key: job.key,
        }),
      });

      const data = (await res.json()) as { success: boolean; message: string };

      if (data.success) {
        setState("success");
        setResultMessage(data.message);
        pushToast({
          title: "Dispatched",
          description: data.message,
          tone: "success",
        });
      } else {
        setState("error");
        setResultMessage(data.message);
        pushToast({
          title: "Dispatch failed",
          description: data.message,
          tone: "error",
        });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      setState("error");
      setResultMessage(msg);
      pushToast({ title: "Dispatch failed", description: msg, tone: "error" });
    }
  };

  return (
    <div className="flex items-start justify-between gap-4 py-4 border-b last:border-b-0">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground">{job.label}</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          {job.description}
        </p>
        {state !== "idle" && state !== "loading" && (
          <div
            className={`flex items-center gap-1 mt-1.5 text-xs font-medium ${state === "success" ? "text-green-600" : "text-destructive"}`}
          >
            {state === "success" ? (
              <CheckCircle className="w-3.5 h-3.5 shrink-0" />
            ) : (
              <XCircle className="w-3.5 h-3.5 shrink-0" />
            )}
            <span>{resultMessage}</span>
          </div>
        )}
      </div>
      <Button
        size="sm"
        variant="outline"
        disabled={!selectedBusinessId || state === "loading"}
        onClick={dispatch}
        className="shrink-0"
      >
        {state === "loading" ? (
          <Loader className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Zap className="w-3.5 h-3.5" />
        )}
        {state === "loading" ? "Sending…" : "Dispatch"}
      </Button>
    </div>
  );
};

type AdminJobsPageProps = {
  jobsData: AdminJobsData;
  adminRoutes: AdminRouteMap;
  csrfToken: string;
};

export const AdminJobsPage: React.FC<AdminJobsPageProps> = ({
  jobsData,
  adminRoutes,
  csrfToken,
}) => {
  const { businesses, jobDefinitions } = jobsData;
  const [selectedBusinessId, setSelectedBusinessId] = React.useState<
    number | null
  >(businesses[0]?.id ?? null);

  const jobsByCategory = React.useMemo(() => {
    const map = new Map<string, AdminJobDefinition[]>();
    for (const job of jobDefinitions) {
      const existing = map.get(job.category) ?? [];
      map.set(job.category, [...existing, job]);
    }
    return map;
  }, [jobDefinitions]);

  return (
    <DashboardPageLayout
      title="Jobs"
      subtitle="Manually dispatch background jobs and emails to a specific account."
      headerActions={
        <ButtonLink
          href={adminRoutes.adminQueueDashboard}
          variant="outline"
          size="sm"
        >
          <LayoutGrid className="w-4 h-4" />
          Queue Dashboard
        </ButtonLink>
      }
    >
      <Card className="mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Target Account</CardTitle>
          <CardDescription>
            Select the business you want to target before dispatching any job.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <select
            className="ds-native-select w-full max-w-sm rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            value={selectedBusinessId ?? ""}
            onChange={(e) => setSelectedBusinessId(Number(e.target.value))}
          >
            {businesses.length === 0 && (
              <option value="" disabled>
                No businesses found
              </option>
            )}
            {businesses.map((b) => (
              <option key={b.id} value={b.id}>
                {b.label}
              </option>
            ))}
          </select>
          {!selectedBusinessId && (
            <p className="text-xs text-muted-foreground mt-2">
              No business selected — dispatch buttons are disabled.
            </p>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {Array.from(jobsByCategory.entries()).map(([category, jobs]) => (
          <Card key={category}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">{category}</CardTitle>
                <Badge variant="secondary">{jobs.length}</Badge>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {jobs.map((job) => (
                <JobRow
                  key={job.key}
                  job={job}
                  selectedBusinessId={selectedBusinessId}
                  dispatchUrl={adminRoutes.adminDispatchJob}
                  csrfToken={csrfToken}
                />
              ))}
            </CardContent>
          </Card>
        ))}
      </div>
    </DashboardPageLayout>
  );
};
